import { motion } from "framer-motion";
import {
  Building2,
  Cpu,
  TrendingUp,
  UserCheck,
  CheckCircle2,
  Sparkles,
  Layers,
} from "lucide-react";

import "../../Style/components/data-flow.css";

const DATA_STREAMS = [
  {
    id: "firmographic",
    title: "Firmographic Data",
    desc: "Industry, Headcount, Revenue, Geo",
    icon: Building2,
    color: "#4a63e7",
  },
  {
    id: "technographic",
    title: "Technographic Data",
    desc: "Installed Tech Stack & Cloud Tools",
    icon: Cpu,
    color: "#06b6d4",
  },
  {
    id: "intent",
    title: "B2B Intent Data",
    desc: "Surge Research & Buying Signals",
    icon: TrendingUp,
    color: "#8b5cf6",
  },
  {
    id: "contact",
    title: "B2B Contact Data",
    desc: "Direct Dials, Verified Emails, Titles",
    icon: UserCheck,
    color: "#f59e0b",
  },
];

export default function DataFlowDiagram() {
  return (
    <div className="flow-container">
      {/* Background Radial Glow */}
      <div className="flow-bg-glow"></div>

      {/* 1. TOP SOURCE NODE: Company */}
      <motion.div
        className="flow-node flow-node--source"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flow-node__pulse"></div>
        <div className="flow-node__icon-wrap">
          <Layers size={20} />
        </div>
        <div className="flow-node__body">
          <span className="flow-node__tag">Input Source</span>
          <h4 className="flow-node__name">Target Company Profile</h4>
        </div>
      </motion.div>

      {/* SVG Connecting Flow Lines with CSS animated dashed gradients */}
      <div className="flow-svg-canvas">
        <svg viewBox="0 0 500 160" fill="none" preserveAspectRatio="none">
          <defs>
            <linearGradient id="beamGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--primary, #4a63e7)" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.8" />
            </linearGradient>
          </defs>

          {/* Lines fanning out from center-top to the 4 nodes */}
          <path d="M 250,0 C 250,50 62,60 62,150" className="flow-path" />
          <path d="M 250,0 C 250,50 188,60 188,150" className="flow-path" />
          <path d="M 250,0 C 250,50 312,60 312,150" className="flow-path" />
          <path d="M 250,0 C 250,50 438,60 438,150" className="flow-path" />
        </svg>
      </div>

      {/* 2. MIDDLE NODES: The 4 Data Categories */}
      <div className="flow-grid">
        {DATA_STREAMS.map((item, idx) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.id}
              className="flow-card"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 + idx * 0.1 }}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
            >
              <div
                className="flow-card__icon"
                style={{
                  color: item.color,
                  backgroundColor: `${item.color}15`,
                  borderColor: `${item.color}30`,
                }}
              >
                <Icon size={18} />
              </div>
              <h5 className="flow-card__title">{item.title}</h5>
              <p className="flow-card__desc">{item.desc}</p>
            </motion.div>
          );
        })}
      </div>

      {/* SVG Connecting Flow Lines converging to bottom node */}
      <div className="flow-svg-canvas">
        <svg viewBox="0 0 500 120" fill="none" preserveAspectRatio="none">
          <path d="M 62,0 C 62,70 250,50 250,110" className="flow-path flow-path--converge" />
          <path d="M 188,0 C 188,60 250,50 250,110" className="flow-path flow-path--converge" />
          <path d="M 312,0 C 312,60 250,50 250,110" className="flow-path flow-path--converge" />
          <path d="M 438,0 C 438,70 250,50 250,110" className="flow-path flow-path--converge" />
        </svg>
      </div>

      {/* 3. BOTTOM TARGET NODE: High Converting Leads */}
      <motion.div
        className="flow-node flow-node--output"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        whileHover={{ scale: 1.02 }}
      >
        <div className="flow-node__icon-wrap flow-node__icon-wrap--emerald">
          <Sparkles size={20} />
        </div>
        <div className="flow-node__body">
          <div className="flow-output-header">
            <span className="flow-node__tag flow-node__tag--emerald">Final Verified Output</span>
            <span className="flow-status">
              <CheckCircle2 size={13} /> High Deliverability
            </span>
          </div>
          <h4 className="flow-node__name">Actionable B2B Leads Engine</h4>
        </div>
      </motion.div>
    </div>
  );
}